import { useState, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import moment from 'moment-timezone'
import Calendar from 'react-calendar'
import { useTranslation } from 'react-i18next'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js'
import { addDays, addHours, startOfDay } from 'date-fns'
import { fromZonedTime, format } from 'date-fns-tz'

import Icon from '../../../Icons'
import TextField from '../../../FormLegacy/FormFieldsV2/TextField'
import AmenityReservationConfirmPopup from '../../../Popups/AmenityReservationConfirmPopup'
import { selectUserData, selectUserRole } from '../../../../reducers/userDataReducer'
import { selectActiveBuilding } from '../../../../reducers/buildingSelectorReducer'
import {
  getPricePerDay,
  isDayAlreadyReserved,
  getTimezonedDate,
  getPricesFromTimestamps,
  getFinalPrice,
  getDateAndPriceForSpecialDates,
  calculateAvailableLocationIds,
  getBookedLocationsDuringPeriod,
} from '../helpers'
import { dayMinutesToHoursMinutes } from '../../../../helpers/helpers'
import { getAssetLink } from '../../../../helpers/get-asset-link'
import API from '../../../../api/api'
import { stripeElementOptions } from '../../../CreditCardContent/helpers'

import TimeSelectView from './TimeSelectView'

const CARD_ERRORS = [
  'Reservation payment error: Your card has expired.',
  'Card token was not provided',
]

function AmenityReservationCalendar({
  amenity,
  userData,
  userRole,
  cancelReservationView,
  displayHeader = true,
  externalDataAsManagement,
  buildingData,
  onValueChange,
  hideReservationButton,
  hideSummary,
  setAvailableLocationIds,
}) {
  const { t } = useTranslation(['client-reservations', 'common'])
  const {
    id,
    name,
    bookingWindow,
    multiDayBooking,
    multidaySlots,
    daysAvailable,
    price,
    endDate,
    startDate,
    managerialBooking,
    unavailableSlots,
    isPaid,
    extraFeeName,
    extraFee,
    timeInterval,
    minTime,
    noticeRequired,
    agreement,
    locations,
    noticePeriod,
  } = amenity
  const specialPricingSlots = multidaySlots.filter((slot) => slot.repeats === false)

  const timezone = buildingData
    ? buildingData.timezone
    : userData.buildingId && userData.buildingId.timezone
  const isAdmin = userRole === 'Administrator'
  const timeout = useRef(null)
  const [selectedStartEnd, setSelectedStartEnd] = useState([])
  const [selectedDaysRange, setSelectedDaysRange] = useState([])
  const [selectedTime, setSelectedTime] = useState(undefined)
  const [timeSelectViewVisible, setTimeSelectViewVisible] = useState(false)
  const [isPaymentCardViewVisible, setPaymentCardViewVisible] = useState(false)
  const [isPaymentFailed, setIsPaymentFailed] = useState(false)
  const [unavailablePeriods, setUnavailablePeriods] = useState([])
  const [amenityReservations, setAmenityReservations] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)
  const [confirmPopupVisible, setConfirmPopupVisible] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [reservingDatesWithPrices, setReservingDatesWithPrices] = useState([])
  const [creditCardData, setCreditCardData] = useState(null)
  const [guestId, setGuestId] = useState(null)
  const [cardOwnerFullName, setCardOwnerFullName] = useState(null)
  const [cardData, setCardData] = useState({
    cardNumber: false,
    cardCvc: false,
    cardExpiry: false,
  })

  useEffect(() => {
    if (onValueChange && selectedStartEnd) {
      onValueChange(selectedStartEnd)
    }
  }, [selectedStartEnd])

  const isAmenityPaid = isPaid || price > 0

  const cardDataVerify = useMemo(() => {
    if (cardData.cardExpiry && cardData.cardNumber && cardData.cardCvc && cardOwnerFullName) {
      return true
    } else {
      return false
    }
  }, [cardData, cardOwnerFullName])

  const stripe = useStripe()
  const elements = useElements()

  const verifyCardDataChanges = (event) => {
    if (event.complete) {
      setCardData({ ...cardData, [event.elementType]: true })
    } else {
      setCardData({ ...cardData, [event.elementType]: false })
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setLoading(true)
    if (elements === null) {
      return
    }
    const data = {
      name: cardOwnerFullName,
    }
    try {
      const cardElement = elements.getElement(CardCvcElement)
      const { token } = await stripe.createToken(cardElement, data)
      setCreditCardData(token)
      setConfirmPopupVisible(true)
      setLoading(false)
    } catch {
      setErrorMessage(t('client-reservations:confirmation.errorCorrectData'))
      timeout.current = setTimeout(() => {
        setErrorMessage(null)
      }, 2000)
    }
  }

  const WEEKDAYS = [
    t('client-reservations:days.sunday'),
    t('client-reservations:days.monday'),
    t('client-reservations:days.tuesday'),
    t('client-reservations:days.wednesday'),
    t('client-reservations:days.thursday'),
    t('client-reservations:days.friday'),
    t('client-reservations:days.saturday'),
  ]

  const actualTzTimestamp = timezone
    ? moment(new Date()).tz(timezone).valueOf()
    : new Date().getTime()

  const bookingWindowEnd = bookingWindow
    ? new Date().setDate(new Date().getDate() + bookingWindow)
    : null
  const preparedEndDate =
    bookingWindowEnd && endDate
      ? Math.min(bookingWindowEnd, endDate)
      : bookingWindowEnd
        ? bookingWindowEnd
        : multiDayBooking
          ? undefined
          : endDate

  // Get locationIds blocked by managerialBooking for a given period
  const getBlockedLocationIdsFromManagerialBooking = (startDate, endDate) => {
    if (!managerialBooking || !startDate) return []

    if (multiDayBooking) {
      const userStartDay = new Date(startDate).setHours(0, 0, 0, 0)
      const userEndDay = endDate ? new Date(endDate).setHours(0, 0, 0, 0) : userStartDay

      return managerialBooking
        .filter((booking) => {
          if (!booking.locationId) return false
          const bookingStartDay = new Date(booking.startDate).setHours(0, 0, 0, 0)
          const bookingEndDay = new Date(booking.endDate).setHours(0, 0, 0, 0)
          return bookingStartDay < userEndDay && bookingEndDay > userStartDay
        })
        .map((booking) => booking.locationId)
    }

    const startTs = new Date(startDate).setHours(0, 0, 0, 0)
    const endTs = endDate
      ? new Date(endDate).setHours(23, 59, 59, 999)
      : new Date(startDate).setHours(23, 59, 59, 999)

    return managerialBooking
      .filter((booking) => {
        if (!booking.locationId) return false
        // Check if managerialBooking period overlaps with selected period
        return booking.startDate <= endTs && booking.endDate >= startTs
      })
      .map((booking) => booking.locationId)
  }

  // Checking if user doesn't selected some of the already reserved days
  const checkAvailability = (selectedDays, firstDaySelected, lastDaySelected) => {
    if (
      selectedDays.find(
        (selectedDay) =>
          isDayAlreadyReserved(
            new Date(selectedDay).setHours(0, 0, 0, 0),
            unavailablePeriods,
            actualTzTimestamp,
            timezone,
          ) === 'fullDay',
      ) ||
      selectedDays.find((selectedDay) => getDayDisabled(selectedDay)) ||
      isDayAlreadyReserved(
        new Date(lastDaySelected).setHours(0, 0, 0, 0),
        unavailablePeriods,
        actualTzTimestamp,
        timezone,
      ) === 'endDay' ||
      (isDayAlreadyReserved(firstDaySelected, unavailablePeriods, actualTzTimestamp, timezone) ===
        'endDay' &&
        isDayAlreadyReserved(
          new Date(lastDaySelected).setHours(0, 0, 0, 0),
          unavailablePeriods,
          actualTzTimestamp,
          timezone,
        ) === 'endDay') ||
      isDayAlreadyReserved(firstDaySelected, unavailablePeriods, actualTzTimestamp, timezone) ===
        'startDay' ||
      (isDayAlreadyReserved(
        new Date(lastDaySelected).setHours(0, 0, 0, 0),
        unavailablePeriods,
        actualTzTimestamp,
        timezone,
      ) === null &&
        selectedDays
          .slice(1)
          .find(
            (selectedDay) =>
              isDayAlreadyReserved(
                new Date(selectedDay).setHours(0, 0, 0, 0),
                unavailablePeriods,
                actualTzTimestamp,
                timezone,
              ) === 'endDay',
          ))
    ) {
      setSelectedDaysRange([])
      setSelectedStartEnd([])
      setAvailableLocationIds([])
      setErrorMessage(t('client-reservations:confirmation.errorDaysAlreadyReserved'))
      timeout.current = setTimeout(() => {
        setErrorMessage(null)
      }, 2000)
    } else if (format(firstDaySelected, 'yyyy-MM-dd') === format(lastDaySelected, 'yyyy-MM-dd')) {
      setAvailableLocationIds([])
      setErrorMessage(t('client-reservations:confirmation.errorSelectTwoDays'))
      timeout.current = setTimeout(() => {
        setErrorMessage(null)
      }, 2000)
    } else {
      setSelectedDaysRange(selectedDays)
      setSelectedStartEnd([firstDaySelected, lastDaySelected])

      // Calculate and save available locationIds for the selected date range
      const availableIds = calculateAvailableLocationIds(
        selectedDays,
        locations,
        amenityReservations,
      )
      // Filter out locationIds blocked by managerialBooking with locationId
      const blockedByManagerial = getBlockedLocationIdsFromManagerialBooking(
        firstDaySelected,
        lastDaySelected,
      )
      const filteredIds = availableIds.filter((id) => !blockedByManagerial.includes(id))
      setAvailableLocationIds(filteredIds)
    }
  }

  // Select startDay, endDay and also set full range between these two
  const setSelectedDays = (startEndCalendarClicked) => {
    if (!multiDayBooking) {
      setSelectedStartEnd([startEndCalendarClicked])
      // Filter out locationIds blocked by managerialBooking with locationId
      const allLocationIds = locations?.map((location) => location.id) || []
      const blockedByManagerial =
        getBlockedLocationIdsFromManagerialBooking(startEndCalendarClicked)
      const filteredIds = allLocationIds.filter((id) => !blockedByManagerial.includes(id))
      setAvailableLocationIds(filteredIds)
      if (!hideReservationButton) {
        setTimeSelectViewVisible(true)
      }
    } else {
      const allDaysInRange = []
      allDaysInRange.push(startEndCalendarClicked[0])
      let nextDay = new Date(
        new Date(startEndCalendarClicked[0]).setDate(
          new Date(startEndCalendarClicked[0]).getDate() + 1,
        ),
      )
      while (
        format(nextDay, 'yyyy-MM-dd') !== format(startEndCalendarClicked[1], 'yyyy-MM-dd') &&
        nextDay <= startEndCalendarClicked[1]
      ) {
        allDaysInRange.push(nextDay)
        nextDay = new Date(new Date(nextDay).setDate(new Date(nextDay).getDate() + 1))
      }
      allDaysInRange.push(startEndCalendarClicked[1])

      checkAvailability(allDaysInRange, startEndCalendarClicked[0], startEndCalendarClicked[1])
    }
  }

  const getAmenityReservations = async () => {
    try {
      const responseData = await API.getAmenityReservations(id, actualTzTimestamp)

      // Collect unavailable periods
      const unavailablePeriods = []

      // 1. Add entries without locationId (blocked for all locations)
      const entriesWithoutLocation = responseData.filter(
        (entry) => !entry.amenity?.locationId && !entry.locationId,
      )
      unavailablePeriods.push(
        ...entriesWithoutLocation.map((entry) => ({
          date: entry.date,
          endDate: entry.endDate,
        })),
      )

      // 2. Check for periods where all locations are booked simultaneously
      if (locations && locations.length > 0) {
        // Get all entries with locationId
        const entriesWithLocation = responseData.filter((entry) => entry.amenity?.locationId)

        if (entriesWithLocation.length > 0) {
          // Get all unique timestamps (start and end dates)
          const timestamps = new Set()
          entriesWithLocation.forEach((entry) => {
            timestamps.add(entry.date)
            timestamps.add(entry.endDate)
          })

          // Sort timestamps to create time intervals
          const sortedTimestamps = Array.from(timestamps).sort((a, b) => a - b)
          const allLocationIds = locations.map((location) => location.id)

          // For each adjacent pair of timestamps, check if all locations are booked
          for (let i = 0; i < sortedTimestamps.length - 1; i++) {
            const intervalStart = sortedTimestamps[i]
            const intervalEnd = sortedTimestamps[i + 1]

            // Use utility function to get booked locations during this interval
            const bookedLocations = getBookedLocationsDuringPeriod(
              entriesWithLocation,
              intervalStart,
              intervalEnd,
            )

            // If all locations are booked during this interval, add it to unavailable periods
            const allLocationsBooked = allLocationIds.every((locationId) =>
              bookedLocations.has(locationId),
            )

            if (allLocationsBooked) {
              unavailablePeriods.push({
                date: intervalStart,
                endDate: intervalEnd,
              })
            }
          }
        }
      }

      // Combine original managerialBooking (without locationId) with newly detected unavailable periods
      // managerialBooking with locationId should not disable dates on calendar
      const allManagerialBookings = [
        ...(managerialBooking || [])
          .filter((booking) => !booking.locationId)
          .map((booking) => ({
            date: booking.startDate,
            endDate: booking.endDate,
          })),
        ...unavailablePeriods,
      ]

      setAmenityReservations(responseData)
      setUnavailablePeriods(allManagerialBookings)
    } catch (err) {
      console.error(err)
    }
  }

  useEffect(() => {
    if (multiDayBooking) {
      getAmenityReservations()
    }
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  // Reset available location IDs when amenity reservations change
  useEffect(() => {
    if (selectedStartEnd.length > 0 && selectedDaysRange.length > 0) {
      const availableIds = calculateAvailableLocationIds(
        selectedDaysRange,
        locations,
        amenityReservations,
      )
      // Filter out locationIds blocked by managerialBooking with locationId
      const blockedByManagerial = getBlockedLocationIdsFromManagerialBooking(
        selectedStartEnd[0],
        selectedStartEnd[1],
      )
      const filteredIds = availableIds.filter((id) => !blockedByManagerial.includes(id))
      setAvailableLocationIds(filteredIds)
    } else if (selectedStartEnd.length === 1) {
      const availableIds = calculateAvailableLocationIds(
        selectedStartEnd,
        locations,
        amenityReservations,
      )
      // Filter out locationIds blocked by managerialBooking with locationId
      const blockedByManagerial = getBlockedLocationIdsFromManagerialBooking(selectedStartEnd[0])
      const filteredIds = availableIds.filter((id) => !blockedByManagerial.includes(id))
      setAvailableLocationIds(filteredIds)
    } else {
      setAvailableLocationIds([])
    }
  }, [amenityReservations])

  const getDayStyling = (date) => {
    if (multiDayBooking) {
      return isDayAlreadyReserved(date, unavailablePeriods, actualTzTimestamp, timezone) ===
        'startDay'
        ? 'reserved-start-day'
        : isDayAlreadyReserved(date, unavailablePeriods, actualTzTimestamp, timezone) === 'endDay'
          ? 'reserved-end-day'
          : isDayAlreadyReserved(date, unavailablePeriods, actualTzTimestamp, timezone) ===
              'fullDay'
            ? 'reserved-full-day'
            : null
    } else {
      return null
    }
  }

  const getDayDisabled = (date) => {
    const localDate = new Date(date)
    const hasUnavailableRegular =
      unavailableSlots &&
      unavailableSlots.regular.some((dateSlot) => {
        const startDate = getTimezonedDate(dateSlot.date, timezone)
        const endDate = getTimezonedDate(dateSlot.endDate, timezone)
        return date >= startDate && date <= endDate
      })

    if (noticePeriod) {
      const noticePeriodStartDate = fromZonedTime(startOfDay(new Date()), timezone)
      const noticePeriodEndDate = addHours(noticePeriodStartDate, noticePeriod)
      const dateStartOfDay = fromZonedTime(startOfDay(localDate), timezone)

      if (dateStartOfDay >= noticePeriodStartDate) {
        if (multiDayBooking) {
          // For multiDayBooking: block any date that has ANY time within notice period
          if (dateStartOfDay < noticePeriodEndDate) {
            return true
          }
        } else {
          // For non-multiDayBooking: block only dates where the END of day is within notice period
          // This means we ignore dates that only partially overlap (like having just 1 hour in notice period)
          const dateEndOfDay = fromZonedTime(
            new Date(
              localDate.getFullYear(),
              localDate.getMonth(),
              localDate.getDate(),
              23,
              59,
              59,
              999,
            ),
            timezone,
          )

          if (dateEndOfDay < noticePeriodEndDate) {
            return true
          }
        }
      }
    }

    const hasUnavailableWeekly =
      unavailableSlots &&
      unavailableSlots.weekly.some((weeklySlot) => weeklySlot.date === new Date(localDate).getDay())
    const isDayDisabled = !daysAvailable.includes(localDate.getDay())

    if (multiDayBooking) {
      const isSpecialPricingDate = specialPricingSlots.some(
        (slot) => format(slot.startDate, 'yyyy-MM-dd') === format(localDate, 'yyyy-MM-dd'),
      )

      if (isSpecialPricingDate) {
        return false
      }

      return hasUnavailableRegular || hasUnavailableWeekly || isDayDisabled
    } else {
      return isDayDisabled
    }
  }

  const onReservationConfirm = async () => {
    const newBookingData = {}
    const finalPriceFromReservingDatesWithExtraFree = getFinalPrice(
      reservingDatesWithPrices,
      extraFee,
    )

    const isZeroPriceAmenity =
      (multiDayBooking && !finalPriceFromReservingDatesWithExtraFree) ||
      (!multiDayBooking && !price)

    if ((isAmenityPaid || extraFee) && !isZeroPriceAmenity) {
      if (
        externalDataAsManagement &&
        ((externalDataAsManagement.residentData &&
          !externalDataAsManagement.residentData.creditCardId) ||
          externalDataAsManagement.guestEmail)
      ) {
        newBookingData.cardToken = creditCardData.id
      } else if (!externalDataAsManagement && !userData.creditCardId) {
        newBookingData.cardToken = creditCardData.id
      }
    }

    if (externalDataAsManagement) {
      if (externalDataAsManagement.residentData) {
        newBookingData.userId = externalDataAsManagement.residentData.userId
      } else if (externalDataAsManagement.guestEmail) {
        newBookingData.guestId = guestId
      }
    }

    if (multiDayBooking) {
      if (!selectedStartEnd[0] || !selectedStartEnd[1]) {
        setConfirmPopupVisible(false)
        return
      }

      newBookingData.date =
        timezone.length > 0
          ? fromZonedTime(selectedStartEnd[0].setHours(0, 0, 0, 0), timezone).getTime()
          : new Date(selectedStartEnd[0].setHours(0, 0, 0, 0)).getTime()

      newBookingData.endDate =
        timezone.length > 0
          ? fromZonedTime(selectedStartEnd[1].setHours(0, 0, 0, 0), timezone).getTime()
          : new Date(selectedStartEnd[1].setHours(0, 0, 0, 0)).getTime()

      newBookingData.price = finalPriceFromReservingDatesWithExtraFree

      try {
        await API.addBooking(id, newBookingData)
        cancelReservationView()
      } catch ({ message }) {
        setErrorMessage(message)
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
        if (CARD_ERRORS.includes(message)) {
          setIsPaymentFailed(true)
        }
        setConfirmPopupVisible(false)
        setLoading(false)
      }
    } else {
      if (!selectedTime) {
        setConfirmPopupVisible(false)
        return
      }

      const { startTime, endTime } = selectedTime

      newBookingData.date =
        timezone.length > 0
          ? fromZonedTime(selectedStartEnd[0].setHours(0, 0, 0, 0), timezone).getTime()
          : new Date(selectedStartEnd[0].setHours(0, 0, 0, 0)).getTime()

      newBookingData.startTime = startTime

      newBookingData.endTime = (endTime || startTime) + timeInterval

      try {
        await API.addBooking(id, newBookingData)
        cancelReservationView()
      } catch ({ message }) {
        setErrorMessage(message)
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
        if (CARD_ERRORS.includes(message)) {
          setIsPaymentFailed(true)
        }
        setConfirmPopupVisible(false)
        setLoading(false)
      }
    }
  }

  const generateConfimationText = () => {
    const reservationForPerson = externalDataAsManagement
      ? externalDataAsManagement.residentData
        ? externalDataAsManagement.residentData.label
        : `${externalDataAsManagement.guestFirstName} ${externalDataAsManagement.guestLastName}`
      : null

    const normalAmenityFinalPrice =
      selectedTime && selectedTime.endTime
        ? price * ((selectedTime.endTime + timeInterval - selectedTime.startTime) / timeInterval)
        : price

    const prefix = externalDataAsManagement
      ? t('client-reservations:confirmation.bookingManagement')
      : t('client-reservations:confirmation.bookingClient')

    if (multiDayBooking && selectedStartEnd.length && selectedStartEnd[1]) {
      const start = format(selectedStartEnd[0], 'MM/dd/yyyy')
      const end = format(selectedStartEnd[1], 'MM/dd/yyyy')
      const finalPriceFromReservingDatesWithExtraFree = getFinalPrice(
        reservingDatesWithPrices,
        extraFee,
      ).toFixed(2)

      const twoDayBody = t('client-reservations:confirmation.twoDayBody', {
        end,
        name,
        prefix,
        price: finalPriceFromReservingDatesWithExtraFree,
        start,
      })

      if (
        reservingDatesWithPrices &&
        reservingDatesWithPrices.some((date) => date.isSpecialPrice)
      ) {
        return `${twoDayBody}
        ${
          extraFee
            ? t('client-reservations:confirmation.extraFeeIncluded', {
                amount: extraFee,
                feeName: extraFeeName,
              })
            : ''
        }
      ${getDateAndPriceForSpecialDates(reservingDatesWithPrices)} ${
        !creditCardData || externalDataAsManagement
          ? ''
          : `\n\n${t('client-reservations:confirmation.chargeCardNotice')}`
      }
      ${
        externalDataAsManagement
          ? `\n${t('client-reservations:confirmation.onBehalfOf', { name: reservationForPerson })}`
          : ''
      }`
      } else {
        return `${twoDayBody}
        ${
          extraFee
            ? `\n${t('client-reservations:confirmation.extraFeeIncluded', {
                amount: extraFee,
                feeName: extraFeeName,
              })}`
            : ''
        } ${
          !creditCardData || externalDataAsManagement
            ? ''
            : `\n\n${t('client-reservations:confirmation.chargeCardNotice')}`
        }
        ${
          externalDataAsManagement
            ? `\n${t('client-reservations:confirmation.onBehalfOf', { name: reservationForPerson })}`
            : ''
        }`
      }
    } else if (selectedStartEnd && selectedTime) {
      const weekday = WEEKDAYS[new Date(selectedStartEnd[0]).getDay()]
      const date = format(new Date(selectedStartEnd[0]), 'MMMM dd, yyyy')
      const start = dayMinutesToHoursMinutes(selectedTime.startTime)
      const end = dayMinutesToHoursMinutes(
        (!selectedTime.endTime ? selectedTime.startTime : selectedTime.endTime) + timeInterval,
      )

      const singleDayBody = isAmenityPaid
        ? t('client-reservations:confirmation.singleDayBodyPaid', {
            date,
            end,
            name,
            prefix,
            price: extraFee
              ? (normalAmenityFinalPrice + extraFee).toFixed(2)
              : normalAmenityFinalPrice.toFixed(2),
            start,
            weekday,
          })
        : t('client-reservations:confirmation.singleDayBodyFree', {
            date,
            end,
            name,
            prefix,
            start,
            weekday,
          })

      return `${singleDayBody}${
        extraFee
          ? `\n${t('client-reservations:confirmation.extraFeeIncluded', {
              amount: extraFee,
              feeName: extraFeeName,
            })}`
          : ''
      } ${
        isAmenityPaid && creditCardData && !externalDataAsManagement
          ? `\n\n${t('client-reservations:confirmation.chargeCardNotice')}`
          : ''
      }
      ${
        externalDataAsManagement
          ? `\n${t('client-reservations:confirmation.onBehalfOf', { name: reservationForPerson })}`
          : ''
      }`
    } else {
      return t('client-reservations:confirmation.none')
    }
  }

  const checkForGuestData = async () => {
    if (externalDataAsManagement && externalDataAsManagement.guestEmail) {
      try {
        const response = await API.createGuest({
          firstName: externalDataAsManagement.guestFirstName,
          lastName: externalDataAsManagement.guestLastName,
          email: externalDataAsManagement.guestEmail,
          phone: externalDataAsManagement.guestPhone,
        })
        setGuestId(response.id)
      } catch {
        setErrorMessage(t('client-reservations:confirmation.errorCheckData'))
        timeout.current = setTimeout(() => {
          setErrorMessage(null)
        }, 2000)
      }
    }
  }

  const verifyNextStepsBasedOnPaymentAndUser = (datesWithPrice) => {
    const finalPrice = getFinalPrice(datesWithPrice, extraFee)
    const isZeroPriceAmenity =
      (multiDayBooking && !finalPrice) || (!multiDayBooking && !price && !extraFee)
    if ((!isAmenityPaid && !extraFee) || isZeroPriceAmenity) {
      setConfirmPopupVisible(true)
      // if Management making reservation
    } else if (externalDataAsManagement) {
      if (externalDataAsManagement.residentData) {
        if (externalDataAsManagement.residentData.creditCardId) {
          setConfirmPopupVisible(true)
        } else {
          setPaymentCardViewVisible(true)
        }
      } else if (externalDataAsManagement.guestEmail) {
        setPaymentCardViewVisible(true)
      }
    } else {
      // if Client making reservation
      if (userData.creditCardId) {
        setConfirmPopupVisible(true)
      } else {
        setPaymentCardViewVisible(true)
      }
    }
    setLoading(false)
  }

  const onReserveClick = async () => {
    if (isPaymentFailed) {
      setErrorMessage(null)
      setPaymentCardViewVisible(true)
      setIsPaymentFailed(false)
      return
    }
    setLoading(true)
    if (multiDayBooking) {
      const datesWithPrice = selectedDaysRange.map((date) =>
        getPricesFromTimestamps(date, multidaySlots, price, timezone),
      )
      setReservingDatesWithPrices(datesWithPrice)
      await checkForGuestData()
      verifyNextStepsBasedOnPaymentAndUser(datesWithPrice)
    } else {
      if (selectedTime.endTime) {
        if (selectedTime.endTime - selectedTime.startTime + timeInterval < minTime) {
          setLoading(false)
          setErrorMessage(
            t('client-reservations:confirmation.errorMinReservationTime', { min: minTime }),
          )

          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
          return
        }
      } else {
        if (timeInterval < minTime) {
          setLoading(false)
          setErrorMessage(t('client-reservations:confirmation.errorDurationMismatch'))
          timeout.current = setTimeout(() => {
            setErrorMessage(null)
          }, 2000)
          return
        }
      }
      await checkForGuestData()
      verifyNextStepsBasedOnPaymentAndUser([])
    }
  }

  const getMinimumDay = () => {
    const isNoticeRequired = noticeRequired
    const disableCurrentDayIfNoticeRequired = isNoticeRequired ? 1 : 0

    const currentDay = addDays(
      fromZonedTime(new Date()),
      disableCurrentDayIfNoticeRequired,
    ).getTime()

    if (isAdmin) {
      return startDate ? fromZonedTime(startDate, timezone) : fromZonedTime(currentDay, timezone)
    }
    return fromZonedTime(currentDay > startDate ? currentDay : startDate, timezone)
  }

  const calendarValue =
    selectedStartEnd.length === 2
      ? selectedStartEnd
      : selectedStartEnd.length
        ? selectedStartEnd[0]
        : null

  return (
    <div className="amenity-calendar">
      {isPaymentCardViewVisible ? (
        <div className="payment-card-stripe-form">
          {displayHeader ? (
            <div className="header">
              <div className="arrow-back" onClick={() => setPaymentCardViewVisible(false)}>
                <Icon icon={'arrow'} />
              </div>
              <span>{t('client-reservations:confirmation.fillCreditCardData')}</span>
            </div>
          ) : null}
          {displayHeader ? null : (
            <div className="tab-header">
              {t('client-reservations:confirmation.fillCreditCardDataColon')}
            </div>
          )}
          <div className="card-elements">
            <div className={`card-element-wrapper ${isLoading ? 'is-loading-field' : ''}`}>
              <CardNumberElement
                id="card-number-element"
                options={{
                  ...stripeElementOptions,
                  placeholder: t('client-reservations:confirmation.cardNumberPlaceholder'),
                }}
                onChange={(event) => verifyCardDataChanges(event)}
              />
            </div>
            <div className="flex-row">
              <div className={`card-element-wrapper ${isLoading ? 'is-loading-field' : ''}`}>
                <CardExpiryElement
                  id="card-expiry-element"
                  options={stripeElementOptions}
                  onChange={(event) => verifyCardDataChanges(event)}
                />
              </div>
              <div className={`card-element-wrapper ${isLoading ? 'is-loading-field' : ''}`}>
                <CardCvcElement
                  id="card-cvc-element"
                  options={{
                    ...stripeElementOptions,
                    placeholder: t('client-reservations:confirmation.cvvPlaceholder'),
                  }}
                  onChange={(event) => verifyCardDataChanges(event)}
                />
              </div>
            </div>
            <div className={isLoading ? 'is-loading-field' : ''}>
              <TextField
                name={'cardholderName'}
                placeholder={t('client-reservations:confirmation.cardholderNamePlaceholder')}
                value={cardOwnerFullName}
                onChange={(event) => setCardOwnerFullName(event.target.value)}
              />
            </div>
          </div>
          {!hideSummary && (
            <div className="reservation-summary-footer">
              {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
              {!hideReservationButton && (
                <div className="reservation-button">
                  <button
                    disabled={!stripe || !elements || !cardDataVerify}
                    className={`button ${isLoading ? 'is-loading' : ''} button-primary`}
                    onClick={(event) => handleSubmit(event)}
                  >
                    {t('client-reservations:actions.reserveNow')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ) : timeSelectViewVisible && selectedStartEnd.length ? (
        <TimeSelectView
          amenity={amenity}
          selectedDay={selectedStartEnd[0]}
          timezone={timezone}
          onBack={() => setTimeSelectViewVisible(false)}
          onReserveClick={onReserveClick}
          setSelectedTimeScopes={(selectedTimeScopes) => setSelectedTime(selectedTimeScopes)}
          displayHeader={displayHeader}
          isLoading={isLoading}
          externalErrorMessage={errorMessage}
        />
      ) : (
        <div className="calendar-conatiner">
          {displayHeader ? (
            <div className="header">
              <div className="arrow-back" onClick={() => cancelReservationView()}>
                <Icon icon={'arrow'} />
              </div>
              <span>{t('client-reservations:confirmation.reservationCalendar')}</span>
            </div>
          ) : null}
          <Calendar
            onChange={(e) => setSelectedDays(e)}
            value={calendarValue}
            locale={'en'}
            calendarType={'US'}
            prev2Label={null}
            next2Label={null}
            minDate={getMinimumDay()}
            maxDate={preparedEndDate ? new Date(preparedEndDate) : undefined}
            selectRange={multiDayBooking}
            tileClassName={({ date }) => getDayStyling(date)}
            tileDisabled={({ date }) => getDayDisabled(date)}
            returnValue={multiDayBooking ? 'range' : 'start'}
            tileContent={({ date }) =>
              multiDayBooking ? (
                <p style={{ fontWeight: 600, fontSize: '10px' }}>
                  {`$${getPricePerDay(date, multidaySlots, price, timezone)}`}
                </p>
              ) : null
            }
          />
          {!hideSummary && (
            <div className="reservation-summary-footer">
              {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
              {multiDayBooking && selectedStartEnd ? (
                <div
                  className="reservation-multiday"
                  style={!selectedStartEnd[0] || !selectedStartEnd[1] ? { opacity: 0.2 } : {}}
                >
                  <div className="check-container">
                    <div className="check-label">
                      {t('client-reservations:confirmation.checkInDate')}
                    </div>
                    <div className="check-date">
                      {selectedStartEnd[0] ? format(selectedStartEnd[0], 'MM/dd/yy') : null}
                    </div>
                  </div>
                  <div className="check-container">
                    <div className="check-label">
                      {t('client-reservations:confirmation.checkOutDate')}
                    </div>
                    <div className="check-date">
                      {selectedStartEnd[1] ? format(selectedStartEnd[1], 'MM/dd/yy') : null}
                    </div>
                  </div>
                </div>
              ) : null}
              {!hideReservationButton && (
                <div className="reservation-button">
                  <button
                    disabled={!selectedStartEnd.length}
                    className={`button ${isLoading ? 'is-loading' : ''} button-primary`}
                    onClick={onReserveClick}
                  >
                    {t('client-reservations:actions.reserveNow')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <AmenityReservationConfirmPopup
        title={t('client-reservations:confirmation.title')}
        cancelText={t('common:actions.cancel')}
        withRulesConfirmation
        confirmText={t('common:actions.confirm')}
        link={agreement && getAssetLink({ path: 'chitown-building', file: agreement })}
        body={generateConfimationText()}
        isOpen={confirmPopupVisible}
        isLoading={isLoading}
        cancelCallback={() => setConfirmPopupVisible(false)}
        confirmCallback={() => {
          setLoading(true)
          onReservationConfirm()
        }}
      />
    </div>
  )
}

AmenityReservationCalendar.propTypes = {
  amenity: PropTypes.object,
  userData: PropTypes.object,
  userRole: PropTypes.string,
  cancelReservationView: PropTypes.func,
  displayHeader: PropTypes.bool,
  externalDataAsManagement: PropTypes.object,
  buildingData: PropTypes.object,
  onValueChange: PropTypes.func,
  hideReservationButton: PropTypes.bool,
  hideSummary: PropTypes.bool,
  locationId: PropTypes.string,
  setAvailableLocationIds: PropTypes.func,
}

export default connect(
  (state) => ({
    userData: selectUserData(state),
    userRole: selectUserRole(state),
    buildingData: selectActiveBuilding(state),
  }),
  {},
)(AmenityReservationCalendar)
